// Утилиты для работы с изображениями

/**
 * Сжимает изображение до указанного размера и качества
 * @param {File|string} file - File объект или base64 строка
 * @param {number} maxWidth - максимальная ширина
 * @param {number} maxHeight - максимальная высота  
 * @param {number} quality - качество сжатия (0.1 - 1.0)
 * @returns {Promise<string>} - сжатое изображение в base64
 */
export function compressImage(file, maxWidth = 1024, maxHeight = 1024, quality = 0.8) {
  return new Promise((resolve, reject) => {
    // Создаем canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = function() {
      // Вычисляем новые размеры с сохранением пропорций
      let { width, height } = calculateNewDimensions(
        img.width, 
        img.height, 
        maxWidth, 
        maxHeight
      );

      // Устанавливаем размеры canvas
      canvas.width = width;
      canvas.height = height;

      // Рисуем сжатое изображение
      ctx.drawImage(img, 0, 0, width, height);

      // Конвертируем в base64 с указанным качеством
      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedBase64);
    };

    img.onerror = function() {
      reject(new Error('Ошибка загрузки изображения'));
    };

    // Загружаем изображение
    if (typeof file === 'string') {
      // Если это уже base64 строка
      img.src = file;
    } else if (file instanceof File) {
      // Если это File объект
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Ошибка чтения файла'));
      reader.readAsDataURL(file);
    } else {
      reject(new Error('Неподдерживаемый тип файла'));
    }
  });
}

/**
 * Вычисляет новые размеры изображения с сохранением пропорций
 */
function calculateNewDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
  let width = originalWidth;
  let height = originalHeight;

  // Если изображение больше максимальных размеров
  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  return { width, height };
}

/**
 * Получает размер base64 строки в байтах
 */
export function getBase64Size(base64String) {
  // Удаляем заголовок data:image/...;base64,
  const base64Data = base64String.split(',')[1] || base64String;
  
  // Вычисляем размер в байтах
  const padding = (base64Data.match(/=/g) || []).length;
  const size = (base64Data.length * 3 / 4) - padding;
  
  return Math.round(size);
}

/**
 * Форматирует размер в читаемый вид
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Проверяет, является ли файл изображением
 */
export function isImageFile(file) {
  if (!file) return false;
  
  const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  return imageTypes.includes(file.type);
}

/**
 * Автоматически сжимает изображение до нужного размера для БД
 * Максимальный размер: 500KB, размеры: 800x800px
 */
export async function compressForDatabase(imageData) {
  const MAX_SIZE_BYTES = 500 * 1024; // 500KB
  const MAX_DIMENSION = 800;
  
  try {
    let quality = 0.9;
    let compressed = imageData;
    
    // Сжимаем до тех пор, пока размер не станет приемлемым
    while (getBase64Size(compressed) > MAX_SIZE_BYTES && quality > 0.1) {
      compressed = await compressImage(
        imageData, 
        MAX_DIMENSION, 
        MAX_DIMENSION, 
        quality
      );
      
      quality -= 0.1;
      
      console.log(`🔄 Сжатие: качество ${quality.toFixed(1)}, размер ${formatFileSize(getBase64Size(compressed))}`);
    }
    
    const finalSize = getBase64Size(compressed);
    console.log(`✅ Финальный размер: ${formatFileSize(finalSize)}`);
    
    if (finalSize > MAX_SIZE_BYTES) {
      console.warn('⚠️ Не удалось сжать изображение до нужного размера');
    }
    
    return compressed;
    
  } catch (error) {
    console.error('❌ Ошибка сжатия изображения:', error);
    throw error;
  }
}
